import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { Receipt, SortConfig, GetReceiptsOptions, FilterGroup } from '../types';

const RECEIPTS_PER_PAGE = 20;

interface UseReceiptsDataProps {
    currentView: string;
    currentPage: number;
    searchTerm: string;
    sortConfig: SortConfig | null;
    filters: {
        tags: FilterGroup;
        businessNames: FilterGroup;
        categories: FilterGroup;
        paymentMethods: FilterGroup;
        currencies: FilterGroup;
        dateFrom: string | null;
        dateTo: string | null;
    };
    isReady: boolean;
}

export const useReceiptsData = ({
    currentView,
    currentPage,
    searchTerm,
    sortConfig,
    filters,
    isReady,
}: UseReceiptsDataProps) => {
    const [receipts, setReceipts] = useState<Receipt[]>([]);
    const [totalReceipts, setTotalReceipts] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);


    const fetchReceipts = useCallback(async () => {
        // Pokud nejsme na pohledu s účtenkami, tento hook nemá co načítat.
        // Komponenty Dashboard a PriceTracker si načítají svá vlastní agregovaná data.
        if (currentView !== 'receipts') {
            setReceipts([]);
            setTotalReceipts(0);
            setTotalPages(0);
            if (isLoading) setIsLoading(false);
            return;
        }

        if (!isReady) return;


        setIsLoading(true);
        setError(null);
        try {
            const options: GetReceiptsOptions = {
                page: currentPage,
                limit: RECEIPTS_PER_PAGE,
                searchTerm: debouncedSearchTerm,
                sortConfig: sortConfig,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                tags_include: filters.tags.include,
                tags_exclude: filters.tags.exclude,
                businessNames_include: filters.businessNames.include,
                businessNames_exclude: filters.businessNames.exclude,
                categories_include: filters.categories.include,
                categories_exclude: filters.categories.exclude,
                paymentMethods_include: filters.paymentMethods.include,
                paymentMethods_exclude: filters.paymentMethods.exclude,
                currencies_include: filters.currencies.include,
                currencies_exclude: filters.currencies.exclude,
            };

            const { receipts: fetchedReceipts, total } = await apiService.getReceipts(options);
            
            setReceipts(fetchedReceipts);
            setTotalReceipts(total);
            setTotalPages(Math.ceil(total / RECEIPTS_PER_PAGE));

        } catch (e: any) {
            console.error("Chyba při načítání účtenek:", e);
            setError(e.message || "Nepodařilo se načíst účtenky.");
        } finally {
            setIsLoading(false);
        }
    }, [
        currentView, 
        currentPage, 
        debouncedSearchTerm, 
        sortConfig, 
        filters,
        isReady,
    ]);

    useEffect(() => {
        fetchReceipts();
    }, [fetchReceipts]);

    return { receipts, setReceipts, totalReceipts, totalPages, isLoading, error, fetchReceipts };
};